import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Card, Tag, Button, Modal, Input, Select, Space, Tooltip,
  message, Alert, Statistic, Row, Col, Empty,
} from 'antd';
import {
  ReloadOutlined, EyeInvisibleOutlined, UndoOutlined, UserOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import useAuthStore from '../store/authStore';
import useServerTable from '../hooks/useServerTable';
import { apiErrorMessage } from '../utils/apiError';
import {
  getAnomalies, getAnomalySummary, getAnomalyChecks,
  ignoreAnomaly, reopenAnomaly, scanAnomalies,
} from '../api/anomalies';

const { TextArea } = Input;

/**
 * Anomali Raporu.
 *
 * Sistemin KENDI kurallarıyla çelişen kayıtları listeler. Kaynağı gecelik
 * `anomaly:scan` taraması (02:30); ekran hesaplama yapmaz, tabloyu okur.
 *
 * ÜÇ DURUM
 *   Açık        bakılması gereken
 *   Yok sayılan "bu bilinçli" denip gerekçe yazılarak kapatılmış
 *   Çözülmüş    sonraki taramada kontrol artık bulmuyor -> kendiliğinden kapandı
 *
 * "Çözülmüş" ASIL mekanizma: yönetici öğrenciye gidip düzeltince liste
 * kendini temizler, kimsenin "tamam" tuşuna basması gerekmez. Bu yüzden
 * ekranda elle "çözüldü işaretle" DÜĞMESİ YOK — olsaydı gerçekte
 * düzeltilmemiş kayıtlar da kapatılabilirdi.
 */

const SIDDET = {
  critical: { renk: 'red',    etiket: 'Kritik' },
  warning:  { renk: 'orange', etiket: 'Uyarı' },
  info:     { renk: 'blue',   etiket: 'Bilgi' },
};

const DURUM = [
  { value: 'open',     label: 'Açık' },
  { value: 'ignored',  label: 'Yok sayılan' },
  { value: 'resolved', label: 'Çözülmüş' },
];

const Anomalies = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const yonetebilir = (user?.permissions || []).includes('anomalies.manage');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [checks, setChecks] = useState([]);

  const [status, setStatus] = useState('open');
  const [category, setCategory] = useState(undefined);
  const [checkKey, setCheckKey] = useState(undefined);

  const [ignoreTarget, setIgnoreTarget] = useState(null);
  const [ignoreNote, setIgnoreNote] = useState('');
  const [ignoring, setIgnoring] = useState(false);
  const [scanning, setScanning] = useState(false);

  const table = useServerTable('anomalies', 20);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await getAnomalySummary();
      setSummary(res.data);
    } catch { /* özet kritik değil, sessiz geç */ }
  }, []);

  useEffect(() => {
    getAnomalyChecks().then(res => setChecks(res.data.checks || [])).catch(() => {});
    fetchSummary();
  }, [fetchSummary]);

  const fetchRows = useCallback(async (o = {}) => {
    setLoading(true);
    try {
      const { data } = await getAnomalies({
        ...table.params(o),
        status,
        category:  category || undefined,
        check_key: checkKey || undefined,
      });
      setRows(data.data);
      setTotal(data.total);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Bulgular yüklenemedi.'));
    } finally {
      setLoading(false);
    }
    // Filtreler bagimlilikta OLMALI - yoksa sayfa degisince filtre sessizce
    // sifirlanir (CLAUDE.md'de yazili stale closure tuzagi).
  }, [table.page, table.pageSize, table.sorter, status, category, checkKey]);

  // fetchRows tum filtreleri ve sayfa/siralama durumunu bagimlilikta
  // tasidigi icin degisince kendisi tetikleniyor (Interns.jsx ile ayni desen).
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleIgnore = async () => {
    if (ignoreNote.trim().length < 5) {
      message.warning('Lütfen neden yok saydığınızı yazın (en az 5 karakter).');
      return;
    }
    setIgnoring(true);
    try {
      await ignoreAnomaly(ignoreTarget.id, ignoreNote.trim());
      message.success('Bulgu yok sayıldı. Kayıt sonradan değişirse tekrar listeye düşer.');
      setIgnoreTarget(null);
      setIgnoreNote('');
      fetchRows();
      fetchSummary();
    } catch (e) {
      message.error(apiErrorMessage(e, 'İşlem başarısız.'));
    } finally {
      setIgnoring(false);
    }
  };

  const handleReopen = async (row) => {
    try {
      await reopenAnomaly(row.id);
      message.success('Bulgu tekrar açıldı.');
      fetchRows();
      fetchSummary();
    } catch (e) {
      message.error(apiErrorMessage(e, 'İşlem başarısız.'));
    }
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      await scanAnomalies(checkKey);
      message.success('Tarama başlatıldı. Sonuçlar birkaç dakika içinde listeye yansıyacak.');
    } catch (e) {
      message.error(apiErrorMessage(e, 'Tarama başlatılamadı.'));
    } finally {
      setScanning(false);
    }
  };

  const columns = [
    {
      title: 'Şiddet',
      dataIndex: 'severity',
      width: 90,
      render: (v) => <Tag color={SIDDET[v]?.renk}>{SIDDET[v]?.etiket || v}</Tag>,
      ...table.sortProps('severity'),
    },
    {
      title: 'Bulgu',
      dataIndex: 'title',
      render: (v, r) => (
        <div>
          <div>{v}</div>
          <div className="text-xs text-gray-500">{r.detail?.aciklama}</div>
          {r.status === 'ignored' && (
            <div className="text-xs" style={{ color: '#8c8c8c', marginTop: 2 }}>
              Yok sayıldı{r.ignored_by ? ` — ${r.ignored_by.name} ${r.ignored_by.surname}` : ''}:
              {' '}<i>{r.ignore_note}</i>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Şube',
      dataIndex: ['branch', 'name'],
      width: 130,
      render: (v) => v || '—',
    },
    {
      title: 'İlk görülme',
      dataIndex: 'first_seen_at',
      width: 130,
      render: (v) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—'),
      ...table.sortProps('first_seen_at'),
    },
    {
      title: 'İşlemler',
      width: 190,
      render: (_, r) => (
        <Space size="small">
          {r.student_id && (
            <Tooltip title="Öğrenci detayını aç ve düzelt">
              <Button size="small" icon={<UserOutlined />} onClick={() => navigate(`/students/${r.student_id}`)}>
                Öğrenciye Git
              </Button>
            </Tooltip>
          )}
          {yonetebilir && r.status === 'open' && (
            <Tooltip title="Bilinçli bir istisnaysa gerekçe yazarak listeden çıkar">
              <Button size="small" icon={<EyeInvisibleOutlined />} onClick={() => setIgnoreTarget(r)}>
                Yok Say
              </Button>
            </Tooltip>
          )}
          {yonetebilir && r.status === 'ignored' && (
            <Button size="small" icon={<UndoOutlined />} onClick={() => handleReopen(r)}>
              Tekrar Aç
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const kategoriSecenekleri = [...new Map(
    checks.map(c => [c.category, { value: c.category, label: c.category_label }])
  ).values()];

  const kontrolSecenekleri = checks
    .filter(c => !category || c.category === category)
    .map(c => ({ value: c.key, label: c.label }));

  const secilenKontrol = checks.find(c => c.key === checkKey);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-2xl font-bold">Anomali Raporu</h2>
        <Space>
          {summary?.son_tarama && (
            <span className="text-xs text-gray-500">
              Son tarama: {dayjs(summary.son_tarama).format('DD.MM.YYYY HH:mm')}
            </span>
          )}
          {yonetebilir && (
            <Button icon={<ReloadOutlined />} loading={scanning} onClick={handleScan}>
              Şimdi Tara
            </Button>
          )}
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="Bu liste sistemin kendi kurallarıyla çelişen kayıtları gösterir."
        description="Düzeltmeyi öğrenci ekranından yapın; bir sonraki taramada bulgu kendiliğinden kapanır. Bilinçli bir istisnaysa gerekçe yazarak yok sayabilirsiniz — kayıt sonradan değişirse tekrar listeye düşer."
      />

      {summary && (
        <Row gutter={16} className="mb-4">
          <Col xs={12} md={6}>
            <Card size="small"><Statistic title="Açık bulgu" value={summary.toplam} /></Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Kritik" value={summary.kritik} valueStyle={{ color: summary.kritik ? '#cf1322' : undefined }} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small"><Statistic title="Yok sayılan" value={summary.yok_sayilan} /></Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title="Kategori"
                value={summary.kategoriler?.filter(k => k.toplam).length || 0}
                suffix={`/ ${summary.kategoriler?.length || 0}`}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card>
        <div className="flex gap-3 mb-4 flex-wrap">
          {/* Filtre degisince ilk sayfaya don - aksi halde 3. sayfadayken
              filtre degistirilince bos liste gorunur. */}
          <Select
            style={{ width: 150 }}
            value={status}
            onChange={(v) => { setStatus(v); table.resetToFirstPage(); }}
            options={DURUM}
          />
          <Select
            style={{ width: 190 }}
            placeholder="Kategori"
            allowClear
            value={category}
            onChange={(v) => { setCategory(v); setCheckKey(undefined); table.resetToFirstPage(); }}
            options={kategoriSecenekleri}
          />
          <Select
            style={{ width: 300 }}
            placeholder="Kontrol"
            allowClear
            value={checkKey}
            onChange={(v) => { setCheckKey(v); table.resetToFirstPage(); }}
            options={kontrolSecenekleri}
          />
        </div>

        {secilenKontrol && (
          <Alert
            type="warning"
            className="mb-3"
            message={secilenKontrol.label}
            description={secilenKontrol.description}
          />
        )}

        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          locale={{
            emptyText: (
              <Empty description={
                status === 'open'
                  ? 'Açık bulgu yok — sistem kurallarıyla uyumlu görünüyor.'
                  : 'Kayıt yok.'
              } />
            ),
          }}
          {...table.tableProps(total)}
        />
      </Card>

      <Modal
        open={!!ignoreTarget}
        title="Bulguyu yok say"
        onCancel={() => { setIgnoreTarget(null); setIgnoreNote(''); }}
        onOk={handleIgnore}
        confirmLoading={ignoring}
        okText="Yok Say"
        cancelText="Vazgeç"
      >
        <p className="text-sm mb-2">{ignoreTarget?.title}</p>
        <Alert
          type="info"
          className="mb-3"
          message="Kayıt sonradan değişirse bu bulgu tekrar listeye düşer."
          description="Örneğin yüzde indirim %10'dan %50'ye çıkarılırsa yok sayma geçersiz olur ve yeniden görünür."
        />
        <TextArea
          rows={3}
          maxLength={1000}
          showCount
          placeholder="Neden bilinçli? (örn. müdür onayıyla burs uygulandı)"
          value={ignoreNote}
          onChange={(e) => setIgnoreNote(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default Anomalies;
